// Spinning Wheel Constants
// This file contains all shared configurations for the spinning wheel feature

export interface Prize {
  id: string;
  name: string;
  displayName: string;
  description: string;
  quota: number;
  remaining: number;
  probability: number;
  color: string;
  icon: string;
  image?: string; // Optional image path for the prize sector
  constantContactTag: string; // Tag name in Constant Contact for tracking claims
}

export interface SpinRecord {
  id: string;
  userEmail: string;
  prizeId: string;
  prizeName: string;
  timestamp: string;
}

export interface SpinResult {
  success: boolean;
  prize?: Prize;
  message?: string;
}

export interface ClaimResult {
  success: boolean;
  message: string;
  needNewEmail?: boolean;
}

// Centralized prize configuration - use this everywhere
// Note: Stock tracking is now done via Constant Contact tags using OAuth
export const DEFAULT_PRIZES: Prize[] = [
  { 
    id: 'E600', 
    name: 'E600', 
    displayName: '🏆 E600 Power Station', 
    description: 'Portable Power Station',
    quota: 1, 
    remaining: 1, 
    probability: 1, 
    color: '#FFD700', 
    icon: '🏆',
image: '/images/common/prizes/E600lfp.webp',
    constantContactTag: 'spinning-wheel-e600'
  },
  { 
    id: 'SP100W', 
    name: 'SP100W', 
    displayName: '☀️ SP100W Solar Panel', 
    description: 'Portable Solar Panel',
    quota: 5, 
    remaining: 5, 
    probability: 5, 
    color: '#4CAF50', 
    icon: '☀️',
image: '/images/common/prizes/sp100W.webp',
    constantContactTag: 'spinning-wheel-sp100w'
  },
  { 
    id: 'P201', 
    name: 'P201', 
    displayName: '🔋 P201 Portable Power Bank', 
    description: 'Portable Power Station',
    quota: 10, 
    remaining: 10, 
    probability: 10, 
    color: '#2196F3', 
    icon: '🔋',
image: '/images/common/prizes/P201.webp',
    constantContactTag: 'spinning-wheel-p201'
  },
  { 
    id: 'DISCOUNT50', 
    name: '$50 Coupon', 
    displayName: '💰 $50 Off Your Next Order', 
    description: '',
    quota: 20, 
    remaining: 20, 
    probability: 20, 
    color: '#FF5722', 
    icon: '💰',
image: '/images/common/prizes/coupon50.webp',
    constantContactTag: 'spinning-wheel-discount50'
  },
  { 
    id: 'HAT', 
    name: 'Hat', 
    displayName: '🧢 Exclusive Pion Power Hat', 
    description: '',
    quota: 30, 
    remaining: 30, 
    probability: 30, 
    color: '#9C27B0', 
    icon: '🧢',
image: '/images/common/prizes/hat.webp',
    constantContactTag: 'spinning-wheel-hat'
  },
  { 
    id: 'TOOLBOX', 
    name: 'Insulated Tool Box', 
    displayName: '🧰 Insulated Tool Box', 
    description: '',
    quota: 30, 
    remaining: 30, 
    probability: 30, 
    color: '#607D8B', 
    icon: '🧰',
image: '/images/common/prizes/toolbox.webp',
    constantContactTag: 'spinning-wheel-toolbox'
  },
  { 
    id: 'DISCOUNT20', 
    name: '$20 Coupon', 
    displayName: '💵 $20 Off Your Next Order', 
    description: '',
    quota: 50, 
    remaining: 50, 
    probability: 50, 
    color: '#795548', 
    icon: '💵',
image: '/images/common/prizes/coupon20.webp',
    constantContactTag: 'spinning-wheel-discount20'
  },
  { 
    id: 'DISCOUNT10', 
    name: '$10 Coupon', 
    displayName: '🎁 $10 Off Your Next Order', 
    description: '',
    quota: 9999, 
    remaining: 9999, 
    probability: 54, 
    color: '#E91E63', 
    icon: '🎁',
image: '/images/common/prizes/coupon10.webp',
    constantContactTag: 'spinning-wheel-discount10'
  }
];

// Constant Contact configuration
export const CONSTANT_CONTACT_CONFIG = {
  TAG_PREFIX: 'spinning-wheel-'
};

// Cookie configuration
export const SPINNING_WHEEL_COOKIE = 'spinning-wheel-shown=v1';
export const COOKIE_MAX_AGE = 2592000; // 30 days in seconds

// Animation configuration
export const ANIMATION_CONFIG = {
  SPIN_DURATION: 3000, // 3 seconds
  MIN_ROTATION: 1800, // 5 full rotations minimum
  MAX_ADDITIONAL_ROTATION: 360 // Up to 1 additional rotation
};

// Validation patterns
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Error messages
export const ERROR_MESSAGES = {
  INVALID_EMAIL: 'Please provide a valid email address',
  ALREADY_PARTICIPATED: 'This email address has already participated in our spinning wheel promotion. Please try with a different email address.',
  NO_PRIZES_AVAILABLE: 'Sorry, all prizes have been claimed!',
  NETWORK_ERROR: 'Network error. Please try again later.',
  GENERIC_ERROR: 'Unable to process your request at this time. Please try again later.',
  CONSTANT_CONTACT_ERROR: 'Failed to connect to email service. Please try again later.',
  OAUTH_NOT_CONFIGURED: 'Constant Contact OAuth not configured. Please complete setup at /newsletter-setup'
}; 